import React, { useEffect, useState } from "react";
import { Modal, Button, Input, Form } from "antd";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import IntlCurrencyInput from "react-intl-currency-input";
import './style.css';
import paymentService from "../../service/payment.service";
import DialogDeConfirmação from "../dialog-confirm/dialogDeConfirmação";
import DialogConfirme from "../dialog-confirm/dialog-confirm";
import driverService from "../../service/driver.service";
import freightService from "../../service/freight.service";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getDriverEditPath,
  hasBankValue,
  validateDriverBankForPaymentRequest,
} from "./driverBankValidation";
import PaymentEmailFailureAlert from "./PaymentEmailFailureAlert";
import {
  buildEmailFailurePersistence,
  buildEmailSuccessPersistence,
  isPaymentRequestStatus,
  resendPaymentRequestEmail,
  savePaymentThenSendRequestEmail,
} from "./paymentRequestEmail";
import { expenseModalThemeVars } from "../../theme/globalStyle/globalStyle";

const fieldIconSx = {
  fontSize: 20,
  color: "var(--expense-highlight, #FF3100)",
};

const ExpenseIconSelect = React.forwardRef(
  ({ icon, className = "", children, status, value, ...props }, ref) => (
    <div
      className={`payment-expense-modal__control${
        status === "error" ? " payment-expense-modal__control--error" : ""
      }`}
    >
      <span className="payment-expense-modal__icon" aria-hidden>
        {icon}
      </span>
      <select
        ref={ref}
        className={`form-select payment-expense-modal__select ${className}`.trim()}
        {...props}
        value={value ?? ""}
      >
        {children}
      </select>
      <span className="payment-expense-modal__chevron" aria-hidden>
        <KeyboardArrowDownIcon sx={{ fontSize: 22, color: "var(--expense-text-secondary)" }} />
      </span>
    </div>
  ),
);
ExpenseIconSelect.displayName = "ExpenseIconSelect";

const ExpenseCurrencyField = React.forwardRef(
  ({ value, onChange, currencyConfig, inputKey, status }, ref) => (
    <div
      className={`payment-expense-modal__currency${
        status === "error" ? " payment-expense-modal__currency--error" : ""
      }`}
      ref={ref}
    >
      <span className="payment-expense-modal__currency-prefix">R$</span>
      <IntlCurrencyInput
        key={inputKey}
        id="InputDinero2"
        currency="BRL"
        config={currencyConfig}
        defaultValue={typeof value === "number" ? value : 0}
        placeholder="0,00"
        className="form-control payment-expense-modal__currency-input"
        onChange={(event, nextValue) => {
          onChange?.(nextValue);
        }}
      />
    </div>
  ),
);
ExpenseCurrencyField.displayName = "ExpenseCurrencyField";

const PaymentModalDespesas = ({ visible, onClose, driverName, type, onSaveResult }) => {
  const [form] = Form.useForm();
  const user = useSelector((state) => state.user);
  const navigate = useNavigate();

  const [record, setRecord] = useState('');
  const [openDialog, setOpenDialog] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openConfirmSendEmail, setOpenConfirmSendEmail] = useState(false);
  const [openEditDriverDialog, setOpenEditDriverDialog] = useState(false);
  const [missingBankFieldsMessage, setMissingBankFieldsMessage] = useState("");
  const [driverIdToEdit, setDriverIdToEdit] = useState("");
  // Bumped após save bem-sucedido para forçar remount dos inputs não controlados
  // (ex.: IntlCurrencyInput) e garantir que o form volte ao estado inicial.
  const [formKey, setFormKey] = useState(0);
  const [savedPaymentId, setSavedPaymentId] = useState(null);
  const [emailFailedFlag, setEmailFailedFlag] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [lastSavedEmailContext, setLastSavedEmailContext] = useState(null);
  const [openDialogErro, setOpenDialogErro] = useState(false);
  const [erroMessage, setErroMessage] = useState("");
  const [erroDetail, setErroDetail] = useState("");

  // Máscara monetária BR sem símbolo — o "R$" fica no prefixo visual do campo.
  const currencyConfig = {
    locale: "pt-BR",
    formats: {
      number: {
        BRL: {
          style: "decimal",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      },
    },
  };

  const freightOrder = record?.items?.[0]?.order;
  const freightLabel = freightOrder
    ? `Frete: ${freightOrder}`
    : "Frete: Status Não Encontrado";

  useEffect(() => {
    if (driverName) {
      setRecord(driverName);
    }
  }, [driverName]);

  const handleSearchInfo = async (driverId, freightId) => {
    let driverData = null;
    let freightData = null;

    if (!driverId || !freightId) {
      return { driverData, freightData };
    }

    try {
      const driverDoc = await driverService.getDriverAvailable(driverId);
      if (driverDoc.exists) {
        driverData = {
          ...driverDoc.data(),
          driver: driverDoc.data().name || "Motorista não encontrado",
          type: type || "Tipo não definido",
          value: Number(record?.items?.[0]?.value || 0),
        };

        const documentsSnapshot = await driverService.getDriverDocuments(driverId);
        const documents = [];
        documentsSnapshot.forEach((doc) => {
          documents.push({ id: doc.id, ...doc.data() });
        });

        driverData.documents = documents;
      }
    } catch (error) {
      console.error("Erro ao buscar dados do motorista:", error);
    }

    try {
      const freightDoc = await freightService.getFreightById(freightId);
      if (freightDoc.exists) {
        freightData = { ...freightDoc.data() };
      }
    } catch (error) {
      console.error("Erro ao buscar dados do frete:", error);
    }

    return { driverData, freightData };
  };

  const getPaymentItemForBankValidation = () => {
    if (!Array.isArray(record?.items)) return null;
    return (
      record.items.find(
        (item) =>
          item?.driver && String(item.driver).toLowerCase() !== "terceiros",
      ) || record.items[0]
    );
  };

  const validateDriverBankDataForPaymentRequest = async () => {
    const result = await validateDriverBankForPaymentRequest(
      getPaymentItemForBankValidation(),
    );

    setDriverIdToEdit(result.firestoreId || "");
    if (!result.ok) {
      setMissingBankFieldsMessage(result.message);
      setOpenEditDriverDialog(true);
      return false;
    }

    return true;
  };

  const handleGoToDriverEdit = () => {
    setOpenEditDriverDialog(false);
    const path = getDriverEditPath(driverIdToEdit);
    if (!path) return;
    navigate(path);
  };

  const getSelectedStatus = () => {
    const fromForm = form.getFieldValue("status");
    if (hasBankValue(fromForm)) {
      return String(fromForm).toLowerCase().trim();
    }

    if (typeof document !== "undefined") {
      const selectEl = document.querySelector(
        "select.formSelectStatusDespesa.form-select",
      );
      if (selectEl?.value) {
        return String(selectEl.value).toLowerCase().trim();
      }
    }

    return "";
  };

  const onFinish = async (values) => {
    setIsSubmitting(true);
    try { // Cuidado ao alterar esse parte, pode impactar em outras funcionalidades
      const statusAtual = (values?.status || "").toLowerCase();
      const valorAdicional = Number(values?.valorAdicional || 0);
      const shouldSendEmail = isPaymentRequestStatus(statusAtual);

      if (shouldSendEmail) {
        const bankDataOk = await validateDriverBankDataForPaymentRequest();
        if (!bankDataOk) {
          return;
        }
      }

      const paymentData = {
        driver: values.motorista ?? record?.items?.[0]?.driver,
        driverID: record?.items[0]?.driverID,
        idFreight: record?.items[0].idFreight,
        order: record?.items[0].order,
        value: parseFloat(values?.valorAdicional),
        status: values?.status,
        obs: values?.obs || "",
        type: values?.tipo,
        addExpenses: true,
      };

      let createdPaymentId = null;

      const outcome = await savePaymentThenSendRequestEmail({
        shouldSendEmail,
        savePayment: async () => {
          createdPaymentId = await paymentService.save(paymentData);
          setSavedPaymentId(createdPaymentId);
        },
        sendEmail: async () => {
          const driverId = record?.items?.[0]?.driverID;
          const freightId = record?.items?.[0]?.idFreight;
          const { driverData, freightData } = await handleSearchInfo(driverId, freightId);

          if (!driverData || !freightData) {
            return {
              sent: false,
              reason: "Dados insuficientes para enviar e-mail de solicitação de pagamento.",
            };
          }

          const emailContext = {
            driverData,
            freightData,
            obs: values?.obs || "",
            valorAdicional,
            valorOriginal: Number(record?.items?.[0]?.value || 0),
            type: values?.tipo || type || "Tipo não definido",
            driver: values.motorista ?? record?.items?.[0]?.driver,
          };
          setLastSavedEmailContext(emailContext);

          return paymentService.prepareEmail(
            emailContext.driverData,
            emailContext.freightData,
            "Solicitação de Pagamento",
            emailContext.obs,
            emailContext.valorAdicional,
            emailContext.valorOriginal,
            emailContext.valorAdicional,
            0,
            emailContext.type,
            user,
            emailContext.driver
          );
        },
        persistEmailFailure: async (reason) => {
          if (!createdPaymentId) return;
          await paymentService.update({
            id: createdPaymentId,
            ...buildEmailFailurePersistence(reason),
          });
          setEmailFailedFlag(true);
        },
        persistEmailSuccess: async () => {
          if (!createdPaymentId) return;
          await paymentService.update({
            id: createdPaymentId,
            ...buildEmailSuccessPersistence(),
          });
          setEmailFailedFlag(false);
        },
      });

      onSaveResult(true);
      form.resetFields();
      setFormKey((k) => k + 1);

      if (outcome.emailFailed) {
        setEmailFailedFlag(true);
        setOpenDialog(true);
        return;
      }

      setEmailFailedFlag(false);
      onClose();
    } catch (error) {
      console.error("Erro ao salvar status:", error);
      setOpenDialogErro(true);
      setErroMessage("Erro ao salvar pagamento");
      setErroDetail("Não foi possível salvar o pagamento. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendPaymentEmail = async () => {
    if (!savedPaymentId || resendingEmail) return;
    setResendingEmail(true);
    try {
      const outcome = await resendPaymentRequestEmail({
        sendEmail: async () => {
          if (lastSavedEmailContext) {
            return paymentService.prepareEmail(
              lastSavedEmailContext.driverData,
              lastSavedEmailContext.freightData,
              "Solicitação de Pagamento",
              lastSavedEmailContext.obs,
              lastSavedEmailContext.valorAdicional,
              lastSavedEmailContext.valorOriginal,
              lastSavedEmailContext.valorAdicional,
              0,
              lastSavedEmailContext.type,
              user,
              lastSavedEmailContext.driver
            );
          }

          const driverId = record?.items?.[0]?.driverID;
          const freightId = record?.items?.[0]?.idFreight;
          const { driverData, freightData } = await handleSearchInfo(driverId, freightId);
          if (!driverData || !freightData) {
            return { sent: false, reason: "Dados insuficientes para reenviar e-mail." };
          }

          return paymentService.prepareEmail(
            driverData,
            freightData,
            "Solicitação de Pagamento",
            "",
            0,
            Number(record?.items?.[0]?.value || 0),
            0,
            0,
            type || "Tipo não definido",
            user,
            record?.items?.[0]?.driver
          );
        },
        persistEmailFailure: async (reason) => {
          await paymentService.update({
            id: savedPaymentId,
            ...buildEmailFailurePersistence(reason),
          });
          setEmailFailedFlag(true);
        },
        persistEmailSuccess: async () => {
          await paymentService.update({
            id: savedPaymentId,
            ...buildEmailSuccessPersistence(),
          });
          setEmailFailedFlag(false);
        },
      });

      if (outcome.emailFailed) {
        setEmailFailedFlag(true);
      }
    } catch (error) {
      console.error("Erro ao reenviar e-mail:", error);
      setEmailFailedFlag(true);
    } finally {
      setResendingEmail(false);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    if (!emailFailedFlag) {
      onClose();
    }
  };

  const handleSaveClick = async () => { //TODO Erro aqui de reload da página
    try {
      const statusAtual = getSelectedStatus();

      // Só abre o modal de e-mail DEPOIS da validação bancária.
      if (statusAtual === "solicitar pagamento" && !openConfirmSendEmail) {
        setOpenConfirmSendEmail(false);
        setIsSubmitting(true);
        const bankDataOk = await validateDriverBankDataForPaymentRequest();
        setIsSubmitting(false);

        if (!bankDataOk) {
          return;
        }

        setOpenConfirmSendEmail(true);
        return;
      }

      form.submit();
    } catch (error) {
      console.error("Erro ao preparar confirmação:", error);
      setIsSubmitting(false);
      setOpenConfirmSendEmail(false);
    }
  };

  const drivers = Array.isArray(record.items)
    ? [...new Set(record.items.map(item => item.driver || "Motorista não encontrado"))]
    : [];

  return (
    <>
    <Modal
      title="Adicionar Despesas"
      open={visible}
      centered
      closeIcon={<CloseIcon sx={{ fontSize: 20 }} />}
      onCancel={() => {
        if (!isSubmitting) onClose();
      }}
      footer={[
        <Button
          key="cancel"
          className="payment-expense-modal__btn-cancel"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancelar
        </Button>,
        <Button
          key="save"
          type="primary"
          className="payment-expense-modal__btn-save"
          loading={isSubmitting}
          onClick={handleSaveClick}
        >
          Salvar
        </Button>,
      ]}
      width={1000}
      zIndex={11000}
      rootClassName="payment-expense-modal-root"
      className="payment-expense-modal"
      classNames={{
        mask: "payment-expense-modal__mask",
        content: "payment-expense-modal__content",
        header: "payment-expense-modal__header",
        body: "payment-expense-modal__body",
        footer: "payment-expense-modal__footer",
      }}
      styles={{
        wrapper: expenseModalThemeVars,
        mask: {
          ...expenseModalThemeVars,
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          background: "rgba(1, 36, 66, 0.45)",
        },
        content: {
          ...expenseModalThemeVars,
          padding: 0,
          display: "flex",
          flexDirection: "column",
          maxHeight: "calc(100vh - 48px)",
          overflow: "hidden",
        },
        header: {
          margin: 0,
          padding: "24px 32px 12px",
          borderBottom: "none",
          flexShrink: 0,
        },
        body: {
          flex: "1 1 auto",
          minHeight: 0,
          maxHeight: "none",
          overflowY: "auto",
          overflowX: "hidden",
          padding: "8px 32px 24px",
        },
        footer: {
          margin: 0,
          padding: "16px 32px 24px",
          borderTop: "1px solid var(--expense-primary-border)",
          flexShrink: 0,
        },
      }}
    >
      <Form
        key={formKey}
        form={form}
        layout="vertical"
        onFinish={onFinish}
        className="payment-expense-modal__form"
        style={expenseModalThemeVars}
        requiredMark={false}
      >
        <PaymentEmailFailureAlert
          visible={emailFailedFlag}
          loading={resendingEmail}
          onResend={handleResendPaymentEmail}
        />

        <Form.Item
          label="Responsável"
          name="motorista"
          className="payment-expense-modal__field"
          rules={[{ required: true, message: "Selecione o responsável" }]}
          getValueFromEvent={(e) => e?.target?.value}
        >
          <ExpenseIconSelect icon={<PersonOutlineOutlinedIcon sx={fieldIconSx} />}>
            <option value="" disabled>
              Selecione um motorista
            </option>
            <option value={drivers[0]?.nome || drivers[0]?.id || drivers[0]}>
              {drivers[0]?.nome || drivers[0]}
            </option>
            <option value="terceiros">Terceiros</option>
          </ExpenseIconSelect>
        </Form.Item>

        <Form.Item
          label="Frete"
          className="payment-expense-modal__field"
          required
        >
          <div className="payment-expense-modal__control payment-expense-modal__control--readonly">
            <span className="payment-expense-modal__icon" aria-hidden>
              <LocalShippingOutlinedIcon sx={fieldIconSx} />
            </span>
            <div className="payment-expense-modal__readonly" title={freightLabel}>
              {freightLabel}
            </div>
          </div>
        </Form.Item>

        <Form.Item
          label="Valor adicional"
          name="valorAdicional"
          className="payment-expense-modal__field"
          initialValue={0}
          rules={[
            { required: true, message: "Informe o valor adicional" },
            {
              validator: (_, value) => {
                if (value == null || Number(value) <= 0) {
                  return Promise.reject(
                    new Error("O valor adicional deve ser maior que zero"),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <ExpenseCurrencyField
            currencyConfig={currencyConfig}
            inputKey={formKey}
          />
        </Form.Item>

        <Form.Item
          label="Status"
          name="status"
          className="payment-expense-modal__field"
          rules={[{ required: true, message: "Por favor, selecione o status" }]}
          getValueFromEvent={(e) => e?.target?.value}
        >
          <ExpenseIconSelect
            icon={<FlagOutlinedIcon sx={fieldIconSx} />}
            className="formSelectStatusDespesa"
          >
            <option value="">Selecione o status</option>
            <option value="pendente">Pendente Liberação</option>
            <option value="bloqueado">Bloqueado</option>
            <option value="solicitar pagamento">Solicitar Pagamento</option>
            <option value="pago">Pago</option>
            <option value="cancelado">Cancelado</option>
          </ExpenseIconSelect>
        </Form.Item>

        <Form.Item
          label="Tipo"
          name="tipo"
          className="payment-expense-modal__field"
          rules={[{ required: true, message: "Selecione o tipo" }]}
          getValueFromEvent={(e) => e?.target?.value}
        >
          <ExpenseIconSelect icon={<BadgeOutlinedIcon sx={fieldIconSx} />}>
            <option value="">Selecione o tipo</option>
            <option value="diaria">Diaria</option>
            <option value="tde">TDE</option>
            <option value="servico chapa">Serciço de Chapa</option>
            <option value="servico lonagem">Serviço de lonagem</option>
            <option value="servico munck">Serviço de munck</option>
            <option value="hospedagem">Hospedagem</option>
            <option value="guindaste">Guindaste</option>
            <option value="deslocamento">Deslocamento</option>
            <option value="descarga paga na empresa">Descarga paga na empresa</option>
            <option value="lavagem de caminhao">Lavagem de caminhão</option>
            <option value="empilhadeira">Empilhadeira</option>
            <option value="outras despesas">Outras despesas</option>
          </ExpenseIconSelect>
        </Form.Item>

        <Form.Item
          label="Observação"
          name="obs"
          className="payment-expense-modal__field payment-expense-modal__field--obs"
        >
          <Input.TextArea
            className="InputTypeText payment-expense-modal__textarea"
            placeholder="Digite sua observação"
            rows={4}
          />
        </Form.Item>
      </Form>
    </Modal>

    <DialogDeConfirmação
      open={openDialog}
      handleClose={handleDialogClose}
      title="Confirmado!"
      message={
        emailFailedFlag
          ? "O pagamento foi salvo. O e-mail não foi enviado devido à instabilidade do SMTP."
          : "A Adicionar Despesas foi realizada com sucesso."
      }
      status="success"
    />
    <DialogDeConfirmação
      open={openDialogErro}
      handleClose={() => setOpenDialogErro(false)}
      title={erroMessage}
      message={erroDetail}
      status="error"
    />
    <DialogConfirme
      open={openConfirmSendEmail}
      handleClose={() => setOpenConfirmSendEmail(false)}
      handleOk={() => {
        setOpenConfirmSendEmail(false);
        form.submit();
      }}
      title="Confirmar solicitação"
      message="Deseja realmente solicitar pagamento e enviar o e-mail agora?"
      status="warning"
    />
    <DialogConfirme
      open={openEditDriverDialog}
      handleClose={() => setOpenEditDriverDialog(false)}
      handleOk={handleGoToDriverEdit}
      title="Dados bancários incompletos"
      message={missingBankFieldsMessage}
      status="warning"
    />
    </>
  );
};

export default PaymentModalDespesas;
